import { useCallback, useState } from 'react'
import { Keyboard } from 'react-native'

import { type NestedSheet, useNestedSheet } from '../hooks/useNestedSheet'

export interface CountrySheet {
  /**
   * The field the list answers. Held after it closes: the sheet dismisses on
   * its own animation, and the rows have to stay drawn while it plays out.
   */
  fieldName?: string
  isOpen: boolean
  open: (fieldName: string) => void
  close: () => void
  /** The handle and backdrop the step feeds to its `BottomSheetModal`. */
  sheet: NestedSheet<'countries'>
}

/**
 * The country list a phone field raises, as a sheet over the step.
 *
 * Opening it drops the keyboard: the sheet covers the field it was typed into,
 * and a keyboard left standing behind it is one the user has to dismiss twice.
 */
export function useCountrySheet(): CountrySheet {
  const sheet = useNestedSheet<'countries'>()
  const [fieldName, setFieldName] = useState<string>()
  const { nestedScreen, openNestedScreen, closeNestedScreen } = sheet
  const isOpen = nestedScreen !== null

  const open = useCallback(
    (name: string) => {
      Keyboard.dismiss()
      setFieldName(name)
      openNestedScreen('countries')
    },
    [openNestedScreen],
  )

  // Guarded rather than passed straight through: gorhom marks a modal
  // dismissed on a `dismiss()` it never presented, and will not re-present it.
  const close = useCallback(() => {
    if (isOpen) {
      closeNestedScreen()
    }
  }, [isOpen, closeNestedScreen])

  return {
    ...(fieldName === undefined ? {} : { fieldName }),
    isOpen,
    open,
    close,
    sheet,
  }
}
